import React, { useState, useRef } from "react";
import { Changes, RaceProgram, BettingInterest } from "@tvg/ts-types/Race";
import { Button, Icon } from "@tvg/design-system";
import { get } from "lodash";
import ChangesCardPro from "../ChangesCardPro";
import {
  ListTitle,
  Title,
  ChangesContainer,
  AnimationWrapper,
  ListItemHeader,
  ListItemHeaderRight,
  NumberOfChanges,
  NextRacesChangesWrapper,
  ListItem,
  EmptyCardWrapper
} from "./styled-components";
import EmptyCaseDialogMessage from "../../EmptyCaseDialogMessage";

interface RaceWithChanges {
  raceNumber?: string;
  changes?: Changes;
  bettingInterests?: BettingInterest[];
}

interface Props {
  currentRace: RaceProgram;
  nextRacesWithChanges: RaceWithChanges[];
}

const emptyDialogText = {
  title: "There are no changes",
  description: "Currently there are no changes for any races at"
};

const computeChangesNumber = (changes: Changes) => {
  const jockeyChangesNr = get(changes, "horse.jockey", [])?.length || 0;
  const scratchedHorsesNr = get(changes, "horse.scratched", [])?.length || 0;
  const surfaceChangesNr =
    (get(changes, "surface.condition", [])?.length || 0) +
    (get(changes, "surface.tempRailDistance", [])?.length || 0) +
    (get(changes, "surface.course", [])?.length || 0) +
    (get(changes, "surface.distance", [])?.length || 0);

  return jockeyChangesNr + surfaceChangesNr + scratchedHorsesNr;
};

const NextRacesChanges = (props: Props) => {
  const animationWrappers = useRef<HTMLElement[]>([]);
  const { currentRace, nextRacesWithChanges } = props;
  const raceTrackCode = currentRace?.track?.trackCode;

  const [expandedElements, setExpandedElements] = useState<String[]>([]);

  const onClickShowAll = () => {
    if (expandedElements.length > 0) {
      setExpandedElements([]);
    } else {
      const raceNumbers = nextRacesWithChanges.map((el) => el.raceNumber);
      setExpandedElements((raceNumbers as String[]) || []);
    }
  };

  const handleOnClickElement = (raceNumber: string = "") => {
    const newExpandedElements = [...expandedElements];
    const expandedElementIndex = expandedElements.indexOf(raceNumber);

    if (expandedElementIndex !== -1) {
      newExpandedElements.splice(expandedElementIndex, 1);
    } else {
      newExpandedElements.push(raceNumber);
    }

    setExpandedElements(newExpandedElements);
  };

  const attachReference = (el: HTMLElement | null) => {
    if (animationWrappers.current.length === nextRacesWithChanges.length) {
      animationWrappers.current = [];
    }
    if (
      !!el &&
      !animationWrappers.current.includes(el) &&
      nextRacesWithChanges.length > 0
    ) {
      animationWrappers.current.push(el);
    }
  };
  return (
    <NextRacesChangesWrapper>
      <ListTitle>
        <Title>All {raceTrackCode} Races</Title>
        <Button
          isMobile
          isDisabled={!nextRacesWithChanges.length}
          variant="tertiary"
          onPress={onClickShowAll}
          qaLabel="show-all-btn"
          paddingRight="0"
        >
          {expandedElements.length ? "Hide All" : "Show All"}
        </Button>
      </ListTitle>
      {nextRacesWithChanges.length ? (
        <ul>
          {nextRacesWithChanges.map((raceWithChanges, index) => {
            const { current } = animationWrappers;
            const animationHeight = current[index as number]?.clientHeight + 12;
            const isExpanded =
              raceWithChanges.raceNumber &&
              expandedElements.includes(raceWithChanges.raceNumber);

            return (
              <ListItem key={raceWithChanges.raceNumber}>
                <ListItemHeader
                  type="button"
                  onClick={() =>
                    handleOnClickElement(raceWithChanges.raceNumber)
                  }
                >
                  <Title>Race {raceWithChanges.raceNumber}</Title>
                  <ListItemHeaderRight>
                    <NumberOfChanges>
                      {raceWithChanges.changes &&
                        computeChangesNumber(raceWithChanges.changes)}
                    </NumberOfChanges>
                    <Icon
                      name="changes"
                      size="s"
                      ml="space-2"
                      mr="space-1"
                      backgroundColor="orange.400"
                    />
                    <Icon
                      name={
                        expandedElements.includes(
                          raceWithChanges.raceNumber ?? ""
                        )
                          ? "chevronUp"
                          : "chevronDown"
                      }
                      size="s"
                    />
                  </ListItemHeaderRight>
                </ListItemHeader>

                <ChangesContainer
                  isShown={!!isExpanded}
                  growHeight={animationHeight}
                >
                  <AnimationWrapper
                    id="animation-wrapper"
                    ref={(el) => attachReference(el)}
                  >
                    <ChangesCardPro
                      changes={raceWithChanges.changes}
                      bettingInterests={raceWithChanges.bettingInterests}
                      isFullWidth
                    />
                  </AnimationWrapper>
                </ChangesContainer>
              </ListItem>
            );
          })}
        </ul>
      ) : (
        <EmptyCardWrapper>
          <EmptyCaseDialogMessage
            title={emptyDialogText.title}
            description={`${emptyDialogText.description} ${currentRace.track?.trackName}`}
            qaLabel="changes-empty-state"
            iconName="bets"
          />
        </EmptyCardWrapper>
      )}
    </NextRacesChangesWrapper>
  );
};

export default NextRacesChanges;
